import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export type StudentUpdateRequestStatus = "PENDING" | "APPROVED" | "REJECTED";

export type FieldChangeDto = {
  fieldName: string;
  oldValue: string;
  newValue: string;
};

export type StudentUpdateRequestDto = {
  id?: number;
  studentId?: number;
  studentName?: string;
  changes?: FieldChangeDto[];
  status?: StudentUpdateRequestStatus;
  requestedBy?: number;
  requestedType?: string;
  requestedAt?: string;
  approvedAt?: string;
};

const API_URL = `${process.env.REACT_APP_API_URL}/student-update-request`;

const createStudentUpdateRequest = async (
  data: StudentUpdateRequestDto
): Promise<StudentUpdateRequestDto> => {
  const response = await axios.post(API_URL, data);
  return response.data;
};

const getStudentUpdateRequests = async (): Promise<StudentUpdateRequestDto[]> => {
  const response = await axios.get(API_URL);
  return response.data;
};

const getStudentPendingRequests = async (): Promise<StudentUpdateRequestDto[]> => {
  const response = await axios.get(`${API_URL}/pending`);
  return response.data;
};

const getStudentRequestWithId = async (studentId: number): Promise<StudentUpdateRequestDto[]> => {
  const response = await axios.get(`${API_URL}/${studentId}`);
  return response.data;
};

const processStudentUpdateRequest = async ({
  studentId,
  status,
}: {
  studentId: number;
  status: "APPROVE" | "REJECT";
}): Promise<string> => {
  const response = await axios.put(`${API_URL}/${studentId}`, null, {
    params: { status },
  });
  return response.data;
};

const getStudentRequestsByStatus = async (
  status: "APPROVED" | "REJECTED"
): Promise<StudentUpdateRequestDto[]> => {
  const response = await axios.get(`${API_URL}/getStatus`, { params: { status } });
  return response.data;
};

export function useStudentUpdateRequest() {
  const queryClient = useQueryClient();

  const createStudentUpdateRequestMutation = useMutation({
    mutationFn: createStudentUpdateRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["student-update-requests"] });
    },
  });

  const useStudentUpdateRequests = (enabled = true) =>
    useQuery({
      queryKey: ["student-update-requests"],
      queryFn: getStudentUpdateRequests,
      enabled,
    });

  const useStudentPendingRequests = (enabled = true) =>
    useQuery({
      queryKey: ["student-update-requests", "pending"],
      queryFn: getStudentPendingRequests,
      enabled,
    });

  const useAllStudentRequests = (enabled = true) =>
    useQuery({
      queryKey: ["student-update-requests", "all-admin"],
      queryFn: getStudentUpdateRequests,
      enabled,
    });

  const useStudentRequestById = (studentId: number | null | undefined, enabled = true) =>
    useQuery({
      queryKey: ["student-update-requests", "student", studentId],
      queryFn: () => getStudentRequestWithId(studentId!),
      enabled: enabled && !!studentId,
    });

  const processStudentUpdateRequestMutation = useMutation({
    mutationFn: processStudentUpdateRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["student-update-requests"] });
    },
  });

  const useStudentRequestsByStatus = (status: "APPROVED" | "REJECTED", enabled = true) =>
    useQuery({
      queryKey: ["student-update-requests", "by-status", status],
      queryFn: () => getStudentRequestsByStatus(status),
      enabled,
    });

  return {
    createStudentUpdateRequestMutation,
    useStudentUpdateRequests,
    useStudentPendingRequests,
    useStudentRequestById,
    processStudentUpdateRequestMutation,
    useAllStudentRequests,
    useStudentRequestsByStatus,
  };
}
